import { useState, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Switch,
  Alert,
  Divider,
  Skeleton,
  FormControlLabel,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Email as EmailIcon,
  EmojiEvents as WinnerIcon,
  Dashboard as BoardIcon,
  Group as GroupIcon,
  CardGiftcard as ReferralIcon,
  AccountBalanceWallet as CreditsIcon,
  Campaign as MarketingIcon,
  Assessment as ReportsIcon,
  MergeType as MergeIcon,
  Celebration as WelcomeIcon,
} from '@mui/icons-material';
import { useDocumentTitle } from '../../hooks/core';
import { useFetch } from '../../hooks/core/useFetch';
import { emailPreferencesApi } from '../../api';
import type { EmailPreferences } from '../../api';

interface PreferenceItem {
  key: keyof Omit<EmailPreferences, 'id' | 'userId' | 'updatedAt' | 'unsubscribedFromAll'>;
  label: string;
  description: string;
  icon: React.ReactNode;
  category: 'boards' | 'account' | 'marketing';
}

const preferenceItems: PreferenceItem[] = [
  {
    key: 'welcomeEmails',
    label: 'Welcome Emails',
    description: 'Onboarding emails when you first join',
    icon: <WelcomeIcon fontSize="small" />,
    category: 'account',
  },
  {
    key: 'boardInvites',
    label: 'Board Invites',
    description: 'When someone invites you to a board',
    icon: <EmailIcon fontSize="small" />,
    category: 'boards',
  },
  {
    key: 'boardCompletions',
    label: 'Board Completions',
    description: 'When a board you joined is completed',
    icon: <BoardIcon fontSize="small" />,
    category: 'boards',
  },
  {
    key: 'winnerNotifications',
    label: 'Winner Notifications',
    description: 'When you win a square',
    icon: <WinnerIcon fontSize="small" />,
    category: 'boards',
  },
  {
    key: 'squareRequestUpdates',
    label: 'Square Request Updates',
    description: 'Updates on your square requests',
    icon: <BoardIcon fontSize="small" />,
    category: 'boards',
  },
  {
    key: 'userJoinedBoardAlerts',
    label: 'Player Joined Alerts',
    description: 'When a player joins your board',
    icon: <GroupIcon fontSize="small" />,
    category: 'boards',
  },
  {
    key: 'mergeRequestAlerts',
    label: 'Merge Request Alerts',
    description: 'Updates on square merge requests',
    icon: <MergeIcon fontSize="small" />,
    category: 'boards',
  },
  {
    key: 'referralRewardAlerts',
    label: 'Referral Rewards',
    description: 'When you earn referral rewards',
    icon: <ReferralIcon fontSize="small" />,
    category: 'account',
  },
  {
    key: 'lowCreditsAlerts',
    label: 'Low Credits Alerts',
    description: 'When your credit balance is low',
    icon: <CreditsIcon fontSize="small" />,
    category: 'account',
  },
  {
    key: 'weeklyReports',
    label: 'Weekly Reports',
    description: 'Weekly summary of your activity',
    icon: <ReportsIcon fontSize="small" />,
    category: 'marketing',
  },
  {
    key: 'marketingEmails',
    label: 'Marketing Emails',
    description: 'Promotions, new features, and updates',
    icon: <MarketingIcon fontSize="small" />,
    category: 'marketing',
  },
];

const categories = [
  { key: 'boards' as const, label: 'Board Notifications' },
  { key: 'account' as const, label: 'Account Notifications' },
  { key: 'marketing' as const, label: 'Marketing & Reports' },
];

export function NotificationPreferencesPage() {
  useDocumentTitle('Notification Preferences');

  const { data: preferences, isLoading, error: fetchError, setData } = useFetch(
    () => emailPreferencesApi.getPreferences(),
    []
  );

  const [saving, setSaving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleToggle = useCallback(async (key: string, value: boolean) => {
    if (!preferences) return;
    setSaving(key);
    setError(null);
    setSuccess(null);

    // Optimistic update
    const previousPrefs = { ...preferences };
    setData({ ...preferences, [key]: value });

    try {
      const updated = await emailPreferencesApi.updatePreferences({ [key]: value });
      setData(updated);
    } catch {
      // Revert on failure
      setData(previousPrefs);
      setError('Failed to update preference. Please try again.');
    } finally {
      setSaving(null);
    }
  }, [preferences, setData]);

  const handleUnsubscribeAll = useCallback(async () => {
    if (!preferences) return;
    setSaving('unsubscribedFromAll');
    setError(null);
    setSuccess(null);

    const newValue = !preferences.unsubscribedFromAll;
    const previousPrefs = { ...preferences };

    // When unsubscribing from all, toggle all off; when re-subscribing, toggle all on
    const updatedPrefs = { ...preferences, unsubscribedFromAll: newValue };
    if (newValue) {
      preferenceItems.forEach(item => {
        updatedPrefs[item.key] = false;
      });
    }
    setData(updatedPrefs);

    try {
      const input: Record<string, boolean> = { unsubscribedFromAll: newValue };
      if (newValue) {
        preferenceItems.forEach(item => {
          input[item.key] = false;
        });
      } else {
        preferenceItems.forEach(item => {
          input[item.key] = true;
        });
      }
      const updated = await emailPreferencesApi.updatePreferences(input);
      setData(updated);
      setSuccess(newValue ? 'Unsubscribed from all emails.' : 'Re-subscribed to all emails.');
    } catch {
      setData(previousPrefs);
      setError('Failed to update preferences. Please try again.');
    } finally {
      setSaving(null);
    }
  }, [preferences, setData]);

  if (isLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Skeleton variant="circular" width={32} height={32} />
          <Skeleton variant="text" width={280} height={40} />
        </Box>
        <Skeleton variant="text" width={400} height={24} sx={{ mb: 4 }} />
        <Paper sx={{ p: 3 }}>
          {[...Array(6)].map((_, i) => (
            <Skeleton key={i} variant="rectangular" height={56} sx={{ mb: 2, borderRadius: 1 }} />
          ))}
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }} data-testid="notification-preferences-page">
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <NotificationsIcon color="primary" sx={{ fontSize: 32 }} />
        <Typography variant="h4">Notification Preferences</Typography>
      </Box>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Control which email notifications you receive from SquareUp.
      </Typography>

      {(fetchError || error) && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 3 }}>
          {error || 'Failed to load notification preferences.'}
        </Alert>
      )}
      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}

      {preferences && (
        <>
          {categories.map((category) => {
            const items = preferenceItems.filter(item => item.category === category.key);
            return (
              <Paper key={category.key} sx={{ p: 3, mb: 3 }} data-testid={`category-${category.key}`}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  {category.label}
                </Typography>
                {items.map((item, index) => (
                  <Box key={item.key}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        py: 1.5,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                        <Box sx={{ color: 'text.secondary' }}>
                          {item.icon}
                        </Box>
                        <Box>
                          <Typography variant="body1">{item.label}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {item.description}
                          </Typography>
                        </Box>
                      </Box>
                      <Switch
                        checked={preferences[item.key]}
                        onChange={(_, checked) => handleToggle(item.key, checked)}
                        disabled={saving !== null || preferences.unsubscribedFromAll}
                        data-testid={`toggle-${item.key}`}
                        inputProps={{ 'aria-label': item.label }}
                      />
                    </Box>
                    {index < items.length - 1 && <Divider />}
                  </Box>
                ))}
              </Paper>
            );
          })}

          <Paper sx={{ p: 3 }} data-testid="unsubscribe-section">
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Box>
                <Typography variant="h6" color="error">
                  Unsubscribe from All
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {preferences.unsubscribedFromAll
                    ? 'You are unsubscribed from all email notifications.'
                    : 'Stop receiving all email notifications from SquareUp.'}
                </Typography>
              </Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={preferences.unsubscribedFromAll}
                    onChange={handleUnsubscribeAll}
                    disabled={saving !== null}
                    color="error"
                    data-testid="toggle-unsubscribedFromAll"
                    inputProps={{ 'aria-label': 'Unsubscribe from all' }}
                  />
                }
                label=""
              />
            </Box>
          </Paper>
        </>
      )}
    </Box>
  );
}
